import { useCallback, useMemo, useState } from 'react';
import { useHouseContext } from '@/context/HouseContext.tsx';
import { useAuth } from '@/context/AuthContext.tsx';
import { usePropertyRoles } from '@/hooks/usePropertyRoles.ts';
import { ClassifiedHouse, groupPropertiesByOwnership } from '@/utils/properties/propertyGroupingUtils.ts';
import { House } from '@/client';
import { useAddPropertyFlow } from '@/hooks/properties/useAddPropertyFlow.ts';
import { useAppNavigation } from '@/context/AppNavigationContext.tsx';
import { isHouseSetupComplete } from '@/utils/houseUtils.ts';
import { useNavigate } from '@tanstack/react-router';
import { propertyNavigateOptions } from '@/routes';
import { splitAddress } from '@/utils/properties/addressUtils.ts';
import type { PropertySortMode } from '@/utils/properties/propertySortUtils.ts';
import { sortProperties } from '@/utils/properties/propertySortUtils.ts';
import { useLockedPropertyIds } from '@/hooks/properties/usePropertyPaywallState.ts';
import { useProSubscription } from '@/hooks/payment/useProSubscription.ts';
import { useOutgoingTransferPropertyIds } from '@/hooks/settings/usePendingTransfer.ts';
import { PropertySelectorGroupKey, PropertySelectorLabel } from './propertySelectorSections.ts';

const PROPERTY_SELECTOR_SORT_MODE: PropertySortMode = 'recent-asc';
const PROPERTY_SELECTOR_SEARCH_MIN_PROPERTIES = 3;

export interface PropertySelectorGroupModel {
	key: string;
	title: string;
	houses: ClassifiedHouse[];
	lockedCount: number;
}

/**
 * Centralized model for Property Selector components
 *
 * Encapsulates property grouping, search state, selection handlers,
 * role-based visibility, and navigation side-effects so Desktop and Mobile
 * UIs don't need to receive large prop lists.
 */
export function usePropertySelectorModel() {
	const { houses, selectedHouse, setSelectedHouseId } = useHouseContext();
	const { user, isSuperAdmin } = useAuth();
	const transferringPropertyIds = useOutgoingTransferPropertyIds();
	const { canAddNewProperties } = usePropertyRoles();
	const {
		startAddProperty,
		confirmCheckout,
		cancelCheckout,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		isCheckingOut,
		canStartFlow,
	} = useAddPropertyFlow();

	const lockedPropertyIds = useLockedPropertyIds();
	const {
		initiateCheckout: startSubscribeCheckout,
		isCheckingOut: isSubscribeCheckingOut,
		isConfirmationOpen: isSubscribeConfirmationOpen,
		savedCard: subscribeSavedCard,
		pendingAmountCents: subscribePendingAmountCents,
		confirmCheckout: confirmSubscribeCheckout,
		cancelCheckout: cancelSubscribeCheckout,
	} = useProSubscription();
	const isHouseLocked = useCallback(
		(houseId: number) => lockedPropertyIds?.has(houseId) ?? false,
		[lockedPropertyIds],
	);

	const [searchQuery, setSearchQuery] = useState('');

	const filterBySearch = useCallback(
		(houses: ClassifiedHouse[]) => {
			if (!searchQuery.trim()) return houses;
			const q = searchQuery.toLowerCase();
			return houses.filter((house) => house.address.toLowerCase().includes(q));
		},
		[searchQuery],
	);

	const createGroups = useCallback(
		(properties: ClassifiedHouse[]): PropertySelectorGroupModel[] => {
			if (isSuperAdmin) {
				return [
					{
						key: PropertySelectorGroupKey.Owned,
						title: PropertySelectorLabel.Owned,
						houses: properties,
						lockedCount: 0,
					},
				].filter((group) => group.houses.length > 0);
			}

			const { owned, sharedWithUser, archived } = groupPropertiesByOwnership(properties, user?.id, {
				sortSharedByAccessCreatedAt: false,
			});

			return [
				{
					key: PropertySelectorGroupKey.Owned,
					title: PropertySelectorLabel.Owned,
					houses: owned,
					lockedCount: 0,
				},
				{
					key: PropertySelectorGroupKey.Shared,
					title: PropertySelectorLabel.Shared,
					houses: sharedWithUser,
					lockedCount: sharedWithUser.filter((house) => isHouseLocked(house.id)).length,
				},
				{
					key: PropertySelectorGroupKey.Archive,
					title: PropertySelectorLabel.Archive,
					houses: archived,
					lockedCount: archived.filter((house) => isHouseLocked(house.id)).length,
				},
			].filter((group) => group.houses.length > 0);
		},
		[isSuperAdmin, user?.id, isHouseLocked],
	);

	// A building archive (onboarded=false) is excluded by createGroups, so no extra filter is needed.
	const visibleProperties = houses;
	const totalProperties = useMemo(() => {
		return createGroups(visibleProperties).reduce((total, group) => total + group.houses.length, 0);
	}, [createGroups, visibleProperties]);
	const showSearch = totalProperties >= PROPERTY_SELECTOR_SEARCH_MIN_PROPERTIES;

	const groups = useMemo(() => {
		const searchSource = showSearch ? filterBySearch(visibleProperties) : visibleProperties;
		const filteredProperties = sortProperties(searchSource, PROPERTY_SELECTOR_SORT_MODE);
		return createGroups(filteredProperties);
	}, [createGroups, filterBySearch, showSearch, visibleProperties]);

	const formattedAddress = useMemo(() => {
		if (!selectedHouse) return '';
		return splitAddress(selectedHouse.address).street;
	}, [selectedHouse]);

	const selectedHouseId = selectedHouse?.id ?? 0;

	const { navigateToSetupProperty } = useAppNavigation();
	const navigate = useNavigate();

	const handleHouseSelect = useCallback(
		(house: House) => {
			setSelectedHouseId(house.id);
			if (!isHouseSetupComplete(house)) {
				navigateToSetupProperty();
			} else {
				// Navigate directly using the house's public_id to avoid a race condition
				// where context hasn't updated yet after setSelectedHouseId.
				navigate(propertyNavigateOptions(house.public_id));
			}
		},
		[setSelectedHouseId, navigateToSetupProperty, navigate],
	);

	return {
		// state
		searchQuery,
		setSearchQuery,

		// derived
		groups,
		totalProperties,
		showSearch,
		selectedHouseId,
		formattedAddress,
		transferringPropertyIds,

		// locked / subscribe
		startSubscribeCheckout,
		isSubscribeCheckingOut,
		isSubscribeConfirmationOpen,
		subscribeSavedCard,
		subscribePendingAmountCents,
		confirmSubscribeCheckout,
		cancelSubscribeCheckout,
		isHouseLocked,

		// actions
		handleHouseSelect,
		startAddProperty,
		confirmCheckout,
		cancelCheckout,
		isConfirmationOpen,
		savedCard,
		pendingPayment,
		isCheckingOut,
		canStartFlow,

		canAddNewProperties,
	} as const;
}
